import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2';

const AlertAll = () => {
  const [mous, setMous] = useState([]);
  const [patents, setPatents] = useState([]);
  const [nearExpiryMous, setNearExpiryMous] = useState([]);
  const [nearExpiryPatents, setNearExpiryPatents] = useState([]);
  const [loading, setLoading] = useState(false);

  const sendExpiryEmail = async (id, title, expiryDate, expiryDays, recipientEmail, type) => {
    let toField = '';
    if (Array.isArray(recipientEmail)) {
      toField = recipientEmail.join(', ');
    } else if (typeof recipientEmail === 'string') {
      toField = recipientEmail;
    }

    if (!toField || toField.trim().length === 0) {
      console.warn(`No valid recipients found for ${type} ID: ${id}`);
      return;
    }

    const emailPayload = {
      to: toField,
      subject: `ALERT: ${type.toUpperCase()} Near Expiry (ID: ${id})`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ffccd5; border-radius: 10px; background-color: #fffafb;">
              <h2 style="color: #c9184a; border-bottom: 2px solid #c9184a; padding-bottom: 10px; margin-top: 0;">
                <span style="font-size: 1.5rem; margin-right: 8px;">⚠️</span> Near-Expiry Expiration Alert
              </h2>
              <p>Dear Sona R&D Portal Partner / Inventor,</p>
              <p>This is an automated notification that the following <strong>${type.toUpperCase()}</strong> is nearing its expiration date.</p>
              
              <table style="width: 100%; border-collapse: collapse; margin: 20px 0; background-color: #ffffff; border-radius: 5px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                <tr style="background-color: #f8f9fa;">
                  <th style="padding: 10px; border: 1px solid #eee; text-align: left; font-weight: 600; width: 35%;">Item Type</th>
                  <td style="padding: 10px; border: 1px solid #eee;">${type.toUpperCase()}</td>
                </tr>
                <tr>
                  <th style="padding: 10px; border: 1px solid #eee; text-align: left; font-weight: 600;">ID</th>
                  <td style="padding: 10px; border: 1px solid #eee;">${id}</td>
                </tr>
                <tr style="background-color: #f8f9fa;">
                  <th style="padding: 10px; border: 1px solid #eee; text-align: left; font-weight: 600;">Title</th>
                  <td style="padding: 10px; border: 1px solid #eee; text-transform: uppercase;">${title}</td>
                </tr>
                <tr>
                  <th style="padding: 10px; border: 1px solid #eee; text-align: left; font-weight: 600;">Expiry Date</th>
                  <td style="padding: 10px; border: 1px solid #eee; color: #d90429; font-weight: bold;">${new Date(expiryDate).toLocaleDateString()}</td>
                </tr>
                <tr style="background-color: #f8f9fa;">
                  <th style="padding: 10px; border: 1px solid #eee; text-align: left; font-weight: 600;">Remaining Days</th>
                  <td style="padding: 10px; border: 1px solid #eee; color: #d90429; font-weight: bold;">${expiryDays} Days</td>
                </tr>
              </table>

              <p>Please take the necessary renewal action or complete all pending documentation before the expiration date.</p>
              <hr style="border: 0; border-top: 1px solid #eee; margin: 20px 0;" />
              <p style="font-size: 0.8rem; color: #777;">This is an automated message from the Sona R&D Portal. Please do not reply directly to this email.</p>
            </div>
          </body>
        </html>
      `
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/email/send`, emailPayload);
      console.log(`Email sent successfully via backend for ${type} ID: ${id}`, response.data);
    } catch (error) {
      console.error(`Error sending email via backend for ${type} ID: ${id}`, error.response ? error.response.data : error.message);
      throw error;
    }
  };
  

  const sendAllAlerts = async (type) => {
    let items = type === 'mou' ? nearExpiryMous : nearExpiryPatents;
  
    // Filter out entries where the email is "N/A" or empty
    items = items.filter(item => item.ptnEmail !== "N/A" && item.ptnEmail.length > 0);
  
    // If no valid emails are found, show alert and return
    if (items.length === 0) {
      Swal.fire({
        title: 'No Valid Emails Found!',
        text: `No valid recipient emails found for ${type.toUpperCase()}s. Alerts cannot be sent.`,
        icon: 'warning',
        confirmButtonText: 'Ok',
      });
      return;
    }
  
    console.log(`Sending alert emails for the following ${type.toUpperCase()}s:`, items);
  
    const promises = items.map((item) => {
      const expiryDate = type === 'mou' ? item.dateOfEnd : item.renewalDate;
      const expiryDays = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
      return sendExpiryEmail(
        type === 'mou' ? item.mouId : item.id,
        item.title,
        expiryDate,
        expiryDays,
        item.ptnEmail,
        type
      );
    });
  
    try {
      await Promise.all(promises);
      Swal.fire({
        title: 'Success!',
        text: `Expiry alerts sent successfully for ${type.toUpperCase()}s!`,
        icon: 'success',
        confirmButtonText: 'Ok',
      });
    } catch (error) {
      console.error(`Error sending some emails for ${type.toUpperCase()}s:`, error);
      Swal.fire({
        title: 'Error!',
        text: `Some expiry alert emails for ${type.toUpperCase()}s failed to send. Please try again.`,
        icon: 'error',
        confirmButtonText: 'Retry',
      });
    }
  };
  
  

  // Fetch MOUs and Patents data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch MOUs

        const mouResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/mous?size=${localStorage.getItem("mou_total")}`);
        const allMous = mouResponse.data._embedded?.mous || [];
        setMous(allMous);
        const today = new Date();
        const ninetyDaysLater = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days from today
        
        const filteredMous = allMous.filter(mou => {
          const expiryDate = new Date(mou.dateOfEnd);
          return expiryDate >= today && expiryDate <= ninetyDaysLater;
        });
        // Process emails for MOUs
        const updatedMous = await Promise.all(
          filteredMous.map(async (mou) => {
            if (!mou.sonaPartners) return { ...mou, ptnEmail: 'N/A' };

            const partners = mou.sonaPartners?.split(',').map(partner => partner.trim()) || [];
            const emailPromises = partners.map(async (partner) => {
              try {
                const emailResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/useremail/${partner}`);
                return emailResponse.data || null;
              } catch (error) {
                console.error(`Error fetching email for ${partner}:`, error);
                return null;
              }
            });

            const emails = await Promise.all(emailPromises);
            const validEmails = emails.filter(email => email);
            return { ...mou, ptnEmail: validEmails.length > 0 ? validEmails : 'N/A' };
          })
        );
        setNearExpiryMous(updatedMous);

        // Fetch Patents
        const patentResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/patents?size=${localStorage.getItem("patent_total")}`);
        const allPatents = patentResponse.data._embedded?.patents || [];
        setPatents(allPatents);

        // Filter Patents nearing renewal in 90 days
        const filteredPatents = allPatents.filter((patent) => {
          const renewalDate = new Date(patent.renewalDate);
          return renewalDate > today && renewalDate <= new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days
        });

        // Process emails for Patents
        const updatedPatents = await Promise.all(
          filteredPatents.map(async (patent) => {
            if (!patent.inventors) return { ...patent, ptnEmail: 'N/A' };

            const inventors = patent.inventors.split(',').map(inventor => inventor.trim());
            const emailPromises = inventors.map(async (inventor) => {
              try {
                const emailResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/useremail/${inventor}`);
                return emailResponse.data || null;
              } catch (error) {
                console.error(`Error fetching email for ${inventor}:`, error);
                return null;
              }
            });

            const emails = await Promise.all(emailPromises);
            const validEmails = emails.filter(email => email);
            return { ...patent, ptnEmail: validEmails.length > 0 ? validEmails : 'N/A' };
          })
        );
        setNearExpiryPatents(updatedPatents);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data from the database. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scheduleMidMonthAlert = () => {
    const checkInterval = 1000; // Check every second

    const checkTime = () => {
        const now = new Date();
        const isMidMonth = now.getDate() === 1;
        const isTargetTime = now.getHours() === 16 && now.getMinutes() === 30 && now.getSeconds() === 0;

        if (isMidMonth && isTargetTime) {
            //sendAllAlerts('mou')
            //sendAllAlerts('patent')
            clearInterval(interval); // Stop further checks after triggering once
        }
    };

    const interval = setInterval(checkTime, checkInterval);
};

// Run on script load
scheduleMidMonthAlert();

  return (
    <div style={{ padding: '20px', 
      fontFamily: 'Arial, sans-serif', 
      width: '100vw', 
      minHeight: '100vh', 
      boxSizing: 'border-box', 
      backgroundColor: '#f8f9fa' }}>
      <ToastContainer />
      <h3>Send Alerts</h3>
     
      {/* MOUs Section */}
      {/*<button
        style={{
          marginBottom: '20px',
          padding: '10px 15px',
          backgroundColor: '#0d6efd',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
        onClick={() => sendAllAlerts('mou')}
        disabled={loading || nearExpiryMous.length === 0}
      >
        {loading ? 'Loading...' : 'Send Alerts for Near-Expiry MOUs'}
      </button>*/}
<h2>MOU's</h2>
      {nearExpiryMous.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>MOU ID</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>ORGANIZATION</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Expiry Date</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Remaining Days</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>SonaPartner Email(s)</th>
            </tr>
          </thead>
          <tbody>
            {nearExpiryMous.map((mou) => {
              const expiryDays = Math.ceil((new Date(mou.dateOfEnd) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <tr key={mou.mouId}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{mou.mouId}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{mou.ptnOrg}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {new Date(mou.dateOfEnd).toLocaleDateString()}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{expiryDays}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {Array.isArray(mou.ptnEmail) ? mou.ptnEmail.join(', ') : mou.ptnEmail || 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No MOUs are nearing expiry.</p>
      )}

      {/* Patents Section */}
     {/*} <button
        style={{
          marginTop:"5%",          marginBottom: '20px',
          padding: '10px 15px',
          backgroundColor: '#0d6efd',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
        onClick={() => sendAllAlerts('patent')}
        disabled={loading || nearExpiryPatents.length === 0}
      >
        {loading ? 'Loading...' : 'Send Alerts for Near-Expiry Patents'}
      </button>
*/} 
<h2>PATENTS</h2>
      {nearExpiryPatents.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Patent ID</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Title</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Expiry Date</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Remaining Days</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Inventor(s) Email(s)</th>
            </tr>
          </thead>
          <tbody>
            {nearExpiryPatents.map((patent) => {
              const expiryDays = Math.ceil((new Date(patent.renewalDate) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <tr key={patent.id}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{patent.id}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{patent.title}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {new Date(patent.renewalDate).toLocaleDateString()}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{expiryDays}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {Array.isArray(patent.ptnEmail) ? patent.ptnEmail.join(', ') : patent.ptnEmail || 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No patents are nearing expiry.</p>
      )}
    </div>
  );
};

export default AlertAll;